import React, { useEffect, useRef } from "react";
import {
  BehaviorSubject,
  merge,
  of,
  Subject,
  switchMap,
  throttleTime,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
  tap,
  auditTime,
  animationFrameScheduler,
} from "rxjs";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import TrackContainer from "./TrackContainer";
import DimensionsDetector from "../DimensionsDetector";
import Stage from "../../models/Stage/Stage";
import ZoomService from "../../services/zoomService/zoom.service";
import TrackHorizonalGrid from "./TrackHorizonalGrid";
import eventEmitter from "../../../../components/eventEmitter";

const errorHandler = (e) => {
  console.error(e);
  return of(null);
};

function controller({ props$, destroyed$ }) {
  const bBox = { top: 0, left: 0 };
  const tracksContainerEl$ = new BehaviorSubject({ style: {} });
  const gridContainerEl$ = new BehaviorSubject({ style: {} });

  function handleContainerWidthChange(width) {
    const { stage, zoomService: zs } = props$.value;
    const { xPadding } = zs;
    const cameraWidth = width - 2 * xPadding;
    //
    zs.setXRange(xPadding, width - xPadding);
    zs.setXVisRange(xPadding, width - xPadding);
    //
    stage.setWidth(width);
    stage.setXFrustum(-(xPadding / cameraWidth), 1 + xPadding / cameraWidth);
  }

  function adjustElementsVertical() {
    const { stage, zoomService: zs } = props$.value;
    const containerHeight = zs.yScale$.value.r2;
    const contentHeight = zs.yScale$.value.d2;
    const stageHeight = Math.min(containerHeight, contentHeight);
    stage.setHeight(stageHeight);
    stage.renderAll();
  }

  function adjustScroll() {
    const { stage, zoomService: zs } = props$.value;
    const verticalScroll = zs.zoomId$.value.y;
    const tracksEl = tracksContainerEl$.value;
    const gridEl = gridContainerEl$.value;
    tracksEl.style.transform = `translateY(${verticalScroll}px)`;
    gridEl.style.transform = `translateY(${verticalScroll}px)`;
    stage.setScroll(-verticalScroll); //
    stage.renderAll();
  }

  function handleContainerHeightChange(height) {
    const { zoomService: zs } = props$.value;
    zs.setYRange(0, height);
  }

  function handleTracksHeightChange() {
    const { stage, zoomService: zs } = props$.value;
    const { r2 } = zs.yScale$.value;
    const rendererHeight = stage.recalcTotalHeight();
    stage.setHeight(Math.min(r2, rendererHeight));
    zs.setYDomain(0, rendererHeight);
  }

  function getStage() {
    return props$.value?.stage;
  }

  function getTracks() {
    return props$.value?.stage?.tracks;
  }

  function getZoomService() {
    return props$.value.zoomService;
  }

  function updateTrackRanges(x) {
    const tracks = getTracks();
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].visibleRangeUpdated(x);
    }
  }

  function updateTrackStabilized(x) {
    const tracks = getTracks();
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].visRangeStabilized(x);
    }
  }

  function updatePosition(x) {
    const zs = getZoomService();
    const pos = zs.recalcPositionInfo();
    return { ...x, ...pos };
  }

  function updateBBox() {
    if (!gridContainerEl$.value) return;
    bBox.left = Math.round(gridContainerEl$.value.getBoundingClientRect().left);
    bBox.top = Math.round(gridContainerEl$.value.getBoundingClientRect().top);
  }

  function updateMousePosition(e) {
    const x = e.nativeEvent.clientX - bBox.left;
    const y = e.nativeEvent.clientY - bBox.top;

    props$.value.zoomService.setMousePosX(x);
    props$.value.zoomService.setMousePosY(y);
  }

  // on scroll or resize it should reset bBox.
  merge(
    eventEmitter.scrollTracker$,
    eventEmitter.resizeTracker$,
    props$.pipe(switchMap((x) => x.zoomService.zoomId$))
  )
    .pipe(
      debounceTime(100),
      tap(updateBBox),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      map(({ stage }) => stage.tracks),
      distinctUntilChanged(),
      tap(() => handleTracksHeightChange()),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.zoomService.xVisScale$),
      auditTime(0, animationFrameScheduler),
      tap((x) => getStage().setZoomUniforms(x)),
      throttleTime(150, undefined, { leading: true, trailing: true }),
      map(updatePosition),
      tap(updateTrackRanges),
      debounceTime(1000),
      tap(updateTrackStabilized),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.zoomService.yScale$),
      auditTime(0, animationFrameScheduler),
      tap(adjustElementsVertical),
      tap(adjustScroll),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.zoomService.scroll$),
      auditTime(0, animationFrameScheduler),
      tap(adjustScroll),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe();

  return {
    zoomService: props$.pipe(map((x) => x?.zoomService)),
    setTrackContainerEl: (el) => tracksContainerEl$.next(el),
    setGridContainerEl: (el) => gridContainerEl$.next(el) && updateBBox(),
    handleContainerWidthChange,
    handleContainerHeightChange,
    handleTracksHeightChange,
    updateMousePosition,
  };
}

function WebGLRenderer({
  updateMousePosition,
  handleContainerWidthChange,
  handleContainerHeightChange,
  handleTracksHeightChange,
  setTrackContainerEl,
  setGridContainerEl,
  zoomService,
  stage,
}) {
  const rendererRef0 = useRef(null);
  const rendererRef1 = useRef(null);
  const rendererRef2 = useRef(null);
  const trackContainer = useRef(null);
  const gridContainer = useRef(null);
  const scrollContainerRef = useRef(null);
  const { tracks } = stage;
  const topTracks = tracks.filter((x) => x.isStickyTop && !x.isOverlay);
  const mainTracks = tracks.filter((x) => !x.isStickyTop && !x.isOverlay);

  useEffect(() => {
    stage.setTarget(
      rendererRef0.current,
      rendererRef1.current,
      rendererRef2.current
    );
    setTrackContainerEl(trackContainer.current);
    setGridContainerEl(gridContainer.current);
  }, [stage, setTrackContainerEl, setGridContainerEl]);

  useEffect(() => {
    const destroyed$ = new Subject();

    const throttleConf = {
      leading: true,
      trailing: true,
    };

    merge(...tracks.map((track) => track.updated$))
      .pipe(
        throttleTime(50, undefined, throttleConf),
        tap(() => stage.renderAll()),
        takeUntil(destroyed$)
      )
      .subscribe();

    merge(...tracks.map((track) => track.heightUpdated$))
      .pipe(takeUntil(destroyed$))
      .subscribe(handleTracksHeightChange);

    return () => destroyed$.next();
  }, [tracks, handleTracksHeightChange, stage, zoomService]);

  return (
    <>
      <DimensionsDetector
        onWidthChange={handleContainerWidthChange}
        onHeightChange={handleContainerHeightChange}
      />

      <div ref={gridContainer} className="tw-absolute tw-inset-0">
        {tracks
          .filter((x) => x && !x.isOverlay)
          .map((track) => (
            <TrackHorizonalGrid key={track.id} track={track} />
          ))}
      </div>
      <div
        ref={rendererRef0}
        className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-transparent"
      />
      <div
        ref={rendererRef1}
        className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-transparent"
      />
      <div
        ref={rendererRef2}
        className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-transparent"
      />
      <div
        className="tw-absolute tw-flex tw-flex-col tw-inset-0 tw-overflow-hidden"
        onMouseMove={updateMousePosition}
      >
        {topTracks.map((track) => (
          <TrackContainer key={track.id} track={track} />
        ))}
        <div
          ref={scrollContainerRef}
          onScroll={() => (scrollContainerRef.current.scrollTop = 0)}
          className="tw-flex-grow tw-relative tw-overflow-hidden"
        >
          <div ref={trackContainer} className="tw-absolute tw-inset-0">
            {mainTracks.map((track) => (
              <TrackContainer key={track.id} track={track} />
            ))}
            <div className="bg-1 tw-h-screen tw-border-t-4 border-1" />
          </div>
        </div>
      </div>
    </>
  );
}

WebGLRenderer.propTypes = {
  handleContainerWidthChange: PropTypes.func.isRequired,
  handleContainerHeightChange: PropTypes.func.isRequired,
  handleTracksHeightChange: PropTypes.func.isRequired,
  setTrackContainerEl: PropTypes.func.isRequired,
  setGridContainerEl: PropTypes.func.isRequired,
  stage: PropTypes.instanceOf(Stage).isRequired,
  zoomService: PropTypes.instanceOf(ZoomService).isRequired,
  updateMousePosition: PropTypes.func.isRequired,
};

export default xstream(controller)(WebGLRenderer);
